'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useAccountData } from '../_shell/AccountDataProvider';
import { SecurityRow } from './SecurityRow';
import { GoogleSubscriptionLink } from '../../../../src/components/GoogleSubscriptionLink';
import {
  DELETE_ACCOUNT_HREF,
  GOOGLE_SUBSCRIPTIONS_URL,
  PRIVACY_POLICY_URL,
  dataExportMailto,
  passwordChangeUrl,
  privacyRequestMailto,
} from './links';
import styles from './Security.module.css';

export function SecuritySection() {
  const { t } = useTranslation(['profile', 'common']);
  const session = React.useContext(SessionContext);
  const [isGoogleSubLinked, setIsGoogleSubLinked] = React.useState(false);
  const { isLoadingUser, user } = useAccountData();

  const handleChangePassword = React.useCallback(() => {
    session.getManager(TrackingManager).trackProfileEvent('reset_password');
    window.open(passwordChangeUrl(user?.benzingaUid), '_blank');
  }, [session, user?.benzingaUid]);

  return (
    <section>
      <h2 className="acct-section-title">
        {t('Shell.security-privacy', { defaultValue: 'Security & Privacy', ns: 'profile' })}
      </h2>
      <p className="acct-section-sub">
        {t('Shell.security-section-sub', {
          defaultValue: 'Manage how you sign in, and control how your data is used.',
          ns: 'profile',
        })}
      </p>

      <div className="acct-card">
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>
            {t('Shell.security-group-login', { defaultValue: 'Login', ns: 'profile' })}
          </h3>
        </div>
        <div className={styles.rows}>
          <SecurityRow
            action={
              <button
                className={styles.rowAction}
                disabled={isLoadingUser}
                onClick={handleChangePassword}
                type="button"
              >
                {t('Shell.change-password', { defaultValue: 'Change password', ns: 'profile' })}
              </button>
            }
            hint={t('Shell.password-hint', {
              defaultValue: 'Managed on accounts.benzinga.com — opens in a new tab.',
              ns: 'profile',
            })}
            label={t('Profile.password', { defaultValue: 'Password', ns: 'profile' })}
          />
          <SecurityRow
            action={
              isGoogleSubLinked ? (
                <a
                  className={styles.rowAction}
                  href={GOOGLE_SUBSCRIPTIONS_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('Shell.manage', { defaultValue: 'Manage', ns: 'profile' })}
                </a>
              ) : undefined
            }
            label={t('Shell.connected-accounts', { defaultValue: 'Connected accounts', ns: 'profile' })}
          >
            <GoogleSubscriptionLink
              className={styles.googleSubCard}
              onLinkedChange={setIsGoogleSubLinked}
              variant="compact"
            />
          </SecurityRow>
        </div>
      </div>

      <div className={`acct-card ${styles.cardGap}`}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>
            {t('Shell.security-group-privacy', { defaultValue: 'Data & Privacy', ns: 'profile' })}
          </h3>
        </div>
        <div className={styles.rows}>
          <SecurityRow
            action={
              <a className={styles.rowAction} href={privacyRequestMailto(user?.email)}>
                {t('Shell.request-privacy-action', { defaultValue: 'Request privacy action', ns: 'profile' })}
              </a>
            }
            hint={
              <>
                {t('Shell.privacy-hint-before', {
                  defaultValue: 'Review how your data is used in our ',
                  ns: 'profile',
                })}
                <a className={styles.hintLink} href={PRIVACY_POLICY_URL} rel="noopener noreferrer" target="_blank">
                  {t('Shell.privacy-policy', { defaultValue: 'Privacy Policy', ns: 'profile' })}
                </a>
                {t('Shell.privacy-hint-after', {
                  defaultValue: ', or request a privacy action (including “Do not sell or share” / CCPA).',
                  ns: 'profile',
                })}
              </>
            }
            label={t('Shell.privacy-preferences', { defaultValue: 'Privacy preferences', ns: 'profile' })}
            wrap
          />

          <SecurityRow
            action={
              <a className={styles.rowAction} href={dataExportMailto(user?.email)}>
                {t('Shell.request-export', { defaultValue: 'Request export', ns: 'profile' })}
              </a>
            }
            hint={t('Shell.download-data-hint', {
              defaultValue: 'Email our support team to request a copy of your account data.',
              ns: 'profile',
            })}
            label={t('Shell.download-my-data', { defaultValue: 'Download my data', ns: 'profile' })}
            wrap
          />

          <SecurityRow
            action={
              <Link className={`${styles.rowAction} ${styles.rowActionDanger}`} href={DELETE_ACCOUNT_HREF}>
                {t('Shell.delete-account-action', { defaultValue: 'Delete', ns: 'profile' })}
              </Link>
            }
            hint={t('Shell.delete-account-hint', {
              defaultValue: 'Permanently remove your account and data.',
              ns: 'profile',
            })}
            label={t('Shell.delete-account', { defaultValue: 'Delete account', ns: 'profile' })}
            tone="danger"
            wrap
          />
        </div>
      </div>
    </section>
  );
}
